import { type ReactNode, useState } from "react";
import { ChevronDown } from "lucide-react";
import { agruparPorCategoria } from "@bookdental/schemas";
import { cn } from "@/lib/cn";
import { CatalogItemRow } from "./CatalogItemRow";

export type ServicoDoCatalogo = { id: string; label: string; group: string | null; active: boolean };

/**
 * Os serviços do laboratório em cartões por categoria — o mesmo desenho das escalas nas Cores: o
 * cabeçalho liga/desliga a categoria inteira (parcial quando só parte está ativa). As categorias
 * já vêm abertas com o que o laboratório oferece à vista (pedido do Jun); os não oferecidos ficam
 * atrás de "Mostrar os não oferecidos", senão a base inteira afoga o catálogo.
 */
export function ServicosPorCategoria<T extends ServicoDoCatalogo>({
  servicos,
  buscando,
  renderItem,
  onToggleCategoria,
  ocupado,
  umPorLinha = false,
}: {
  servicos: T[];
  /** Com busca, todo cartão com resultado abre sozinho — achar e ainda ter que abrir é atrito. */
  buscando: boolean;
  renderItem: (servico: T) => ReactNode;
  onToggleCategoria: (categoria: string, ligar: boolean) => void;
  ocupado?: boolean;
  /** Um cartão por linha, com os serviços em duas colunas dentro (Catálogo). Sem ele, dois cartões
   *  por linha e serviços em coluna única (onboarding, com a prévia ao lado). */
  umPorLinha?: boolean;
}) {
  const [invertidas, setInvertidas] = useState<Set<string>>(new Set());
  const [comInativos, setComInativos] = useState<Set<string>>(new Set());
  const grupos = agruparPorCategoria(servicos);

  const alternar = (setter: typeof setInvertidas, categoria: string) =>
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(categoria)) next.delete(categoria);
      else next.add(categoria);
      return next;
    });

  if (grupos.length === 0) {
    return (
      <p className="px-2 py-1.5 text-xs text-zinc-400 dark:text-muted-foreground">
        {buscando ? "Nenhum serviço com essa busca." : "Nenhum serviço no catálogo ainda."}
      </p>
    );
  }

  return (
    <div className={cn("grid grid-cols-1 items-start gap-3", !umPorLinha && "md:grid-cols-2")}>
      {grupos.map(({ categoria, itens }) => {
        const ativos = itens.filter((s) => s.active).length;
        const agregado: boolean | "indeterminate" =
          ativos === 0 ? false : ativos === itens.length ? true : "indeterminate";
        // Abre sozinha a que tem serviço oferecido; a sem nenhum começa fechada. O clique inverte.
        const aberta = buscando || (ativos > 0) !== invertidas.has(categoria);
        // Na busca, mostra tudo o que achou; fora dela, os não oferecidos só a pedido.
        const verInativos = buscando || comInativos.has(categoria) || ativos === 0;
        const visiveis = verInativos ? [...itens.filter((s) => s.active), ...itens.filter((s) => !s.active)] : itens.filter((s) => s.active);
        const inativos = itens.length - ativos;
        return (
          <div
            key={categoria}
            className={cn(
              "rounded-lg border p-3",
              umPorLinha ? "border-zinc-200 bg-white shadow-sm dark:border-border dark:bg-card" : "border-zinc-100 dark:border-border/60",
            )}
          >
            <div className="flex items-center gap-1">
              <div className="min-w-0 flex-1">
                <CatalogItemRow
                  label={categoria}
                  destaque
                  sublabel={`${ativos} de ${itens.length} ativos`}
                  enabled={agregado}
                  disabled={ocupado}
                  onToggle={(ligar) => onToggleCategoria(categoria, ligar)}
                />
              </div>
              {!buscando && (
                <button
                  type="button"
                  onClick={() => alternar(setInvertidas, categoria)}
                  aria-expanded={aberta}
                  aria-label={`${aberta ? "Ocultar" : "Ver"} serviços de ${categoria}`}
                  title={aberta ? "Ocultar serviços" : "Ver serviços"}
                  className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-zinc-800 dark:text-muted-foreground dark:hover:bg-white/5 dark:hover:text-foreground"
                >
                  <ChevronDown className={cn("h-4 w-4 transition-transform", aberta && "rotate-180")} />
                </button>
              )}
            </div>
            {aberta && (
              <div className={cn("mt-1 border-t border-zinc-100 pt-1 dark:border-border/60", umPorLinha && "grid items-start gap-x-6 md:grid-cols-2")}>
                {visiveis.map(renderItem)}
              </div>
            )}
            {aberta && !buscando && inativos > 0 && ativos > 0 && (
              <button
                type="button"
                onClick={() => alternar(setComInativos, categoria)}
                className="mt-1.5 text-xs font-semibold text-brand-700 hover:underline dark:text-primary"
              >
                {verInativos ? "Esconder os não oferecidos" : `Mostrar os não oferecidos (${inativos})`}
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}
